import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authorizeClient, client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { IOtherService } from '../../screens/VideoConsultingRoom/VideoMenu/AdditionalServicesTab';

export interface IWorkOrderService {
  work_order_id: number;
  service_id: number;
  person_id: number;
  business_partner_id: number;
  consultation_findings_id: number;
  consultation_investigations_id: number;
  consultation_recommendations_id: number;
  consultation_notes_id: number;
}

export interface WorkOrderServiceState {
  status: string;
  createStatus: string;
  updateStatus: string;
  createdWorkOrderService: IWorkOrderService | null;
  workOrderServices: IWorkOrderService[];
  createRoomResponse: any;
}

const initialState: WorkOrderServiceState = {
  status: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  createdWorkOrderService: null,
  workOrderServices: [],
  createRoomResponse: null,
};

interface CreateWorkOrderServiceBody {
  salesOrderId?: number;
  salesOrderItemId?: number;
  serviceId?: number;
  personId?: number;
  businessPartnerId?: string;
}

export const createWorkOrderService = createAsyncThunk(
  'work-order-service/create',
  async (createWorkOrderServiceBody: CreateWorkOrderServiceBody, { dispatch, rejectWithValue }) => {
    try {
      const createWorkServiceResponse: any = await client.post(
        '/healthpoint/work-order-service-consultation',
        {
          salesOrderId: createWorkOrderServiceBody.salesOrderId,
          salesOrderItemId: createWorkOrderServiceBody.salesOrderItemId,
          serviceId: createWorkOrderServiceBody.serviceId,
          personId: createWorkOrderServiceBody.personId,
          businessPartnerId: createWorkOrderServiceBody.businessPartnerId,
        }
      );
      return createWorkServiceResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const createConsultationRoom = createAsyncThunk(
  'consultation-room/create',
  async (params: any, { rejectWithValue }) => {
    const { accNumber, taId, productOrderId, productOrderItemId } = params;
    try {
      const createWorkServiceResponse: any = await authorizeClient.post(
        `/core/video_consultation/rooms_merchant?merchant_account_number=${accNumber}&ta_id=${taId}&product_order_id=${productOrderId}&product_order_item_id=${productOrderItemId}`
      );
      return createWorkServiceResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

interface VitalSignResult {
  work_order_id: string;
  vital_sign_id: string;
  service_id: string;
  vital_sign_value_result: string | null;
  vital_sign_text_result: string | null;
  vital_sign_external_id: string;
  vital_sign_name: string;
  machine_id: string;
  diagnostic_machine_external_id: string;
  mealtime_id: string;
  biomarker_uom_id: string;
}

interface UpdateStatusOfConsultationBody {
  appointmentStatus?: string;
  workOrderId: number;
  workOrderStatusId: number;
  details: any;
  taId: number;
  transactionId: number;
  salesOrderId?: number;
  salesOrderExternalId?: number;
  externalSalesOrderItemId?: number;
  personEmail: string;
  personAccountNumber: string;
  merchantPersonId?: string;
  merchantAccountNumber?: string;
  doctor?: string;
  isFaceToFace?: boolean;
  personIdCreate?: string;
}

export const updateStatusOfConsultation = createAsyncThunk(
  'update-status-consultation',
  async (
    updateStatusOfConsultationBody: UpdateStatusOfConsultationBody,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      let vitalSignResults: VitalSignResult[] = [];
      let details = {
        ...updateStatusOfConsultationBody.details,
      };
      if (
        state?.auth?.accountInfo?.consultation_with_extra_steps &&
        updateStatusOfConsultationBody.workOrderStatusId === 2
      ) {
        const vitalSignResultsResponse = await client.get(
          `/healthpoint/vital_sign_results/${updateStatusOfConsultationBody.workOrderId}`
        );
        vitalSignResults = vitalSignResultsResponse.data?.vitalSignResults ?? [];
        // return the vital sign with the same id not more than once
        // because maybe in master data is linked with the same machine more than once
        vitalSignResults = vitalSignResults.filter(
          (vs, index, self) => self.findIndex((v) => v.vital_sign_id === vs.vital_sign_id) === index
        );
        // only send the vital signs which have a value attached
        vitalSignResults = vitalSignResults.filter((vs) => vs.vital_sign_value_result !== null);

        // get the consultation Other Services and add them to the details
        const otherServicesesultsResponse = await client.get(
          `/healthpoint/medical-service/${updateStatusOfConsultationBody.workOrderId}`
        );
        let otherServices: IOtherService[] = [];
        if (otherServicesesultsResponse?.data?.medicalServices) {
          otherServices = otherServicesesultsResponse?.data?.medicalServices;
          details = {
            ...details,
            online_consultation_result: {
              ...details.online_consultation_result,
              consultation_services_text: JSON.stringify(
                otherServices.reduce((acc, os) => {
                  acc[os.service_name] = os.remarks;
                  return acc;
                }, {} as { [key: string]: string | undefined })
              ),
            },
          };
        }
      }
      const createWorkServiceResponse: any = await client.put(
        `/healthpoint/update-status-consultation/${state?.auth?.accountInfo?.account_number}`,
        {
          appointmentStatus: updateStatusOfConsultationBody.appointmentStatus,
          merchantPersonId: updateStatusOfConsultationBody.merchantPersonId,
          merchantAccountNumber: updateStatusOfConsultationBody.merchantAccountNumber,
          workOrderId: updateStatusOfConsultationBody.workOrderId,
          workOrderStatusId: updateStatusOfConsultationBody.workOrderStatusId,
          details: details,
          transactionId: updateStatusOfConsultationBody.transactionId,
          taId: updateStatusOfConsultationBody.taId,
          salesOrderId: updateStatusOfConsultationBody.salesOrderId,
          salesOrderExternalId: updateStatusOfConsultationBody.salesOrderExternalId,
          externalSalesOrderItemId: updateStatusOfConsultationBody.externalSalesOrderItemId,
          personIdCreate: updateStatusOfConsultationBody.personIdCreate,
          personEmail: updateStatusOfConsultationBody.personEmail,
          personAccountNumber: updateStatusOfConsultationBody.personAccountNumber,
          doctor: updateStatusOfConsultationBody.doctor,
          isFaceToFace: updateStatusOfConsultationBody.isFaceToFace,
          vitalSignResults:
            vitalSignResults.length > 0
              ? vitalSignResults.map((vsr) => ({
                  property_id: parseInt(vsr.vital_sign_external_id),
                  property_name: vsr.vital_sign_name,
                  property_value: vsr.vital_sign_value_result,
                  machine_id: vsr.diagnostic_machine_external_id,
                  mealtime_id: parseInt(vsr.mealtime_id),
                  biomarker_uom_id: parseInt(vsr.biomarker_uom_id),
                }))
              : undefined,
        }
      );
      return createWorkServiceResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const workOrderServiceSlice = createSlice({
  name: 'workOrderService',
  initialState,
  reducers: {
    resetWorkOrderService: (_) => {
      return initialState;
    },
    resetCreateStatus: (state) => {
      return {
        ...state,
        createStatus: 'idle',
      };
    },
    resetUpdateStatus: (state) => {
      return {
        ...state,
        updateStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createWorkOrderService.pending, (state) => {
        state.createStatus = 'pending';
      })
      .addCase(createWorkOrderService.fulfilled, (state, action) => {
        state.createStatus = 'fulfilled';
        state.createdWorkOrderService = action.payload.data?.workOrderService;
      })
      .addCase(createWorkOrderService.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.createStatus = 'rejected';
      })
      .addCase(updateStatusOfConsultation.pending, (state) => {
        state.updateStatus = 'pending';
      })
      .addCase(updateStatusOfConsultation.fulfilled, (state, action) => {
        state.updateStatus = 'fulfilled';
      })
      .addCase(updateStatusOfConsultation.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.updateStatus = 'rejected';
      })
      .addCase(createConsultationRoom.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(createConsultationRoom.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.createRoomResponse = action.payload.data;
      })
      .addCase(createConsultationRoom.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      });
  },
});

export const { resetWorkOrderService, resetCreateStatus, resetUpdateStatus } =
  workOrderServiceSlice.actions;

export default workOrderServiceSlice.reducer;

export const selectWorkOrderServices = (state: RootState) =>
  state.workOrderServiceConsultation.workOrderServices;
export const selectWorkOrderServicesStatus = (state: RootState) =>
  state.workOrderServiceConsultation.status;
export const selectWorkOrderServiceCreateStatus = (state: RootState) =>
  state.workOrderServiceConsultation.createStatus;
export const selectCreatedWorkOrderService = (state: RootState) =>
  state.workOrderServiceConsultation.createdWorkOrderService;
export const selectConsultationStatusUpdateStatus = (state: RootState) =>
  state.workOrderServiceConsultation.updateStatus;
export const selectCreateRoomResponse = (state: RootState) =>
  state.workOrderServiceConsultation.createRoomResponse;
